import React, { useCallback, useState } from 'react';
import format from 'string-template';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';

import {
  Alert,
  TextField,
} from '@mui/material';
import {
  DatePicker,
  TimePicker,
} from '@mui/lab';

import { combineDateAndTime } from '../../../../../../utils/date';
import useReminderForm from '../../../../../../hooks/useReminderForm';
import Button from '../../../../../../components/Button';
import RecurrenceBlock from '../RecurrenceBlock';

import {
  FormContainer,
  StyledTextField,
  ActionsRow,
} from './styles';
import texts from './texts.json';

const ReminderForm = ({
  initialContent,
  initialSendAt,
  initialRecurrence,
  onSubmit,
  onCancel,
  submitLabel,
}) => {
  const form = useReminderForm({ initialContent, initialSendAt, initialRecurrence });
  const {
    reminderText, setReminderText,
    sendDate, setSendDate,
    sendTime, setSendTime,
    isRecurring, setIsRecurring,
    interval, setInterval,
    byDay, setByDay,
    endDate, setEndDate,
    timeZone,
    nextFireMoment,
    minTime,
    submitDisabled,
    resolveSendAt,
  } = form;

  const [saveError, setSaveError] = useState(null);

  const isStoppingSeries = Boolean(initialRecurrence) && !isRecurring;

  const handleSubmit = useCallback(async () => {
    const { rule, sendAt, isPastDate } = resolveSendAt();
    if (isPastDate || sendAt === null) {
      setSaveError(texts.error.pastDate);
      return;
    }
    setSaveError(null);
    try {
      await onSubmit({
        content: reminderText,
        resolvedSendAt: sendAt,
        rule,
      });
    } catch (_err) {
      setSaveError(texts.error.saveFailed);
    }
  }, [onSubmit, reminderText, resolveSendAt]);

  const stopWarning = isStoppingSeries
    ? format(texts.stopSeriesWarning, {
      date: combineDateAndTime(sendDate.clone(), sendTime).format('ddd, MMM D, h:mm A'),
    })
    : null;

  const onDateChange = (newValue) => { setSendDate(newValue); setSaveError(null); };
  const onTimeChange = (newValue) => { setSendTime(newValue); setSaveError(null); };

  return (
    <FormContainer>
      <TextField
        value={reminderText}
        fullWidth
        multiline
        maxRows={4}
        variant="standard"
        onChange={(e) => setReminderText(e.target.value)}
      />
      <DatePicker
        label={isRecurring ? texts.startDate : texts.selectDate}
        value={sendDate}
        onChange={onDateChange}
        minDate={moment().local()}
        maxDate={moment().local().add(3, 'years')}
        renderInput={(params) => <StyledTextField {...params} fullWidth />}
      />
      <TimePicker
        label={texts.selectTime}
        value={sendTime}
        onChange={onTimeChange}
        minTime={minTime}
        minutesStep={5}
        renderInput={(params) => <StyledTextField {...params} fullWidth />}
      />
      <RecurrenceBlock
        isRecurring={isRecurring}
        onIsRecurringChange={setIsRecurring}
        interval={interval}
        onIntervalChange={setInterval}
        byDay={byDay}
        onByDayChange={setByDay}
        endDate={endDate}
        onEndDateChange={setEndDate}
        startDate={sendDate}
        timeZone={timeZone}
        nextFireMoment={nextFireMoment}
      />
      {stopWarning && <Alert severity="warning">{stopWarning}</Alert>}
      {saveError && <Alert severity="error">{saveError}</Alert>}
      <ActionsRow>
        {onCancel && (
          <Button onClick={onCancel} full={false}>
            {texts.button.cancel}
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          full={false}
          disabled={submitDisabled}
        >
          {submitLabel || texts.button.save}
        </Button>
      </ActionsRow>
    </FormContainer>
  );
};

ReminderForm.propTypes = {
  initialContent: PropTypes.string,
  initialSendAt: PropTypes.number,
  initialRecurrence: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  submitLabel: PropTypes.string,
};

ReminderForm.defaultProps = {
  initialContent: '',
  initialSendAt: undefined,
  initialRecurrence: null,
  onCancel: null,
  submitLabel: '',
};

export default ReminderForm;
